import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import ForwardIcon from '@mui/icons-material/Forward';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { alpha, Box, Button, Grid, Typography, useTheme } from '@mui/material';
import { APIEndpoint } from 'common/enum';
import { universalPostRequest } from 'common/requestHandler';
import { getUsers } from 'common/utils';
import { defaultErrorMessage } from 'config/config';
import { popupNotification } from 'notifications/ToastNotification';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { validateGroupName } from 'validation/validationScheme';

import { getUserInterface } from './utils';
import CustomModal from '../../components/CustomModal';
import CustomIconButton from '../../components/elements/IconButton';
import InputElement from '../../components/elements/InputElement';
import SelectableList from '../../components/ListItemsWithSearch';
import useSelectableItems from '../../hooks/useSelectableList';

interface groupNameInterface {
  groupName: string;
}

const CreateIndividualGroup = () => {
  const [usersList, setUsersList] = useState<getUserInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const theme = useTheme();
  const { companyId, employeeId } = useSelector(
    (state: RootState) => state.auth
  );
  const { control, handleSubmit, reset } = useForm<groupNameInterface>({
    resolver: yupResolver(validateGroupName),
    mode: 'onChange',
    defaultValues: {
      groupName: '',
    } as groupNameInterface,
  });

  const getUsersList = async () => {
    setLoading(true);
    const response = await getUsers(companyId, 'GetIndividual', employeeId);
    if ('error' in response) {
      popupNotification(defaultErrorMessage, false);
    } else {
      setUsersList(response.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    getUsersList();
  }, [companyId]);

  // State for the left and right lists
  const [leftItems, setLeftItems] = useState<getUserInterface[]>(usersList);
  const [rightItems, setRightItems] = useState<getUserInterface[]>([]);

  // Custom hooks for selecting items on both lists
  const {
    selectedItems: selectedLeftItems,
    toggleItem: toggleLeftItem,
    resetSelection: resetLeftSelection,
  } = useSelectableItems();
  const {
    selectedItems: selectedRightItems,
    toggleItem: toggleRightItem,
    resetSelection: resetRightSelection,
  } = useSelectableItems();

  useEffect(() => {
    setLeftItems(usersList); // Update leftItems whenever usersList changes
  }, [usersList]);

  // Handler to move selected items from left to right
  const handleMoveItemsToRight = () => {
    const updatedRightItems = [
      ...rightItems,
      ...leftItems.filter((item) => selectedLeftItems.includes(item.id)),
    ];
    const updatedLeftItems = leftItems.filter(
      (item) => !selectedLeftItems.includes(item.id)
    );
    // Update the state
    setLeftItems(updatedLeftItems);
    setRightItems(updatedRightItems);
    // Clear the selection in the left list
    resetLeftSelection();
  };

  const createGroup = async (data: groupNameInterface) => {
    setCreatingGroup(true);
    const customerId = rightItems.map((item) => item.id);
    const payload = {
      companyCustomerGroupName: data.groupName,
      companyId,
      customerType: 'Individual customer',
      customerId,
      actionName: 'CreateCustomerGroup',
    };
    const response = await universalPostRequest(
      APIEndpoint.createIndividualCustomerGroup,
      payload
    );
    setCreatingGroup(false);
    setRightItems([]);
    setLeftItems(usersList);
    setShowModal(false);
    reset();
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      popupNotification(response.message, true);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <Box display="flex" flexDirection="column" height="100%" gap={2}>
            {/* Left List */}
            <Typography variant="body1" fontWeight="bold" color="textDisabled">
              Users
            </Typography>
            <SelectableList
              items={leftItems}
              selectedItems={selectedLeftItems}
              toggleItem={toggleLeftItem}
              searchVisible={true}
              idKey="id"
              loading={loading}
              labelKey="name"
            />
          </Box>
        </Grid>

        {/* Arrow Button */}
        <Grid item xs={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <CustomIconButton
              tooltipTitle="Move selected users"
              buttonType="primary"
              onClick={handleMoveItemsToRight} // Move items on click
            >
              <ForwardIcon color="primary" fontSize="large" />
            </CustomIconButton>
          </Box>
        </Grid>

        <Grid item xs={5}>
          <Box display="flex" flexDirection="column" height="100%" gap={2}>
            {/* Right List */}
            <Typography variant="body1" fontWeight="bold" color="textDisabled">
              Group members
            </Typography>
            <SelectableList
              items={rightItems}
              selectedItems={selectedRightItems}
              toggleItem={toggleRightItem}
              searchVisible={true}
              showCheckBoxIcon={false}
              idKey="id"
              showDivider={true}
              labelKey="name"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                background: `linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)`,
                borderRadius: 2.5,
                paddingLeft: 3,
                paddingRight: 3,
                paddingTop: 1,
                paddingBottom: 1,
                fontSize: '0.9rem',
                fontWeight: 600,
                textTransform: 'none',
                color: '#1976d2',
                boxShadow: `0 2px 8px ${alpha('#1976d2', 0.1)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                minWidth: 140,
                border: `1px solid ${alpha('#1976d2', 0.15)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, #bbdefb 0%, #e1bee7 100%)`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 15px ${alpha('#1976d2', 0.15)}`,
                  border: `1px solid ${alpha('#1976d2', 0.25)}`,
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: `0 2px 6px ${alpha('#1976d2', 0.1)}`,
                },
                '&:disabled': {
                  background: '#f5f5f5',
                  color: '#bdbdbd',
                  boxShadow: 'none',
                  transform: 'none',
                  border: `1px solid #e0e0e0`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(90deg, transparent, ${alpha('#1976d2', 0.08)}, transparent)`,
                  transition: 'left 0.5s',
                },
                '&:hover::before': {
                  left: '100%',
                },
              }}
              startIcon={
                <GroupAddIcon
                  sx={{
                    fontSize: 18,
                    color: '#1976d2',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                />
              }
              disabled={!rightItems.length}
              onClick={() => setShowModal(true)}
            >
              Create Group
            </Button>
          </Box>
        </Grid>
      </Grid>
      <CustomModal
        primaryButtonAction={handleSubmit(createGroup)}
        primaryButtonLabel="Create group"
        autoClose={false}
        open={showModal}
        primaryButtonLoading={creatingGroup}
        type="info"
        onClose={() => {
          setShowModal(false);
          reset();
        }}
        title="Individual group name"
      >
        <form onSubmit={handleSubmit(createGroup)}>
          <Typography variant="body2" color="textDisabled">
            {' '}
            Type the group name
          </Typography>
          <InputElement
            name="groupName"
            size="small"
            control={control}
            label="Group name"
            placeholder="Individual group name"
            autoFocus={true}
            type="text"
            rules={{ required: true }} // Rules can be added here
          />
        </form>
      </CustomModal>
    </>
  );
};

export default CreateIndividualGroup;
